import React, { useRef, useState, useCallback } from 'react';
import { ContentCard as ContentCardType, PersonSummaryWithPhoto } from '../types';
import ContentCard from './ContentCard';
import { useTop100PopularMovies } from '../hooks/usePopularContent';

interface PopularMoviesCarouselProps {
  onContentClick?: (content: ContentCardType) => void;
  onPersonClick?: (person: PersonSummaryWithPhoto) => void;
  className?: string;
}



const LoadingSkeleton: React.FC = () => (
  <div className="flex gap-4 px-4 md:px-8 pb-4">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="flex-none w-48 sm:w-56 md:w-64">
        <div className="bg-gray-800 rounded-lg animate-pulse">
          <div className="w-full h-56 sm:h-64 md:h-72 bg-gray-700 rounded-lg"></div>
        </div>
      </div>
    ))}
  </div>
);

const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="text-red-400 mb-4">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h3 className="text-white text-lg font-semibold mb-2">Failed to load popular movies</h3>
    <p className="text-gray-400 text-center mb-4">
      We couldn't load the popular movies carousel. Please try again.
    </p>
    <button
      onClick={onRetry}
      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
    >
      Try Again
    </button>
  </div>
);

const PopularMoviesCarousel: React.FC<PopularMoviesCarouselProps> = ({
  onContentClick,
  onPersonClick,
  className = '',
}) => {
  const { popularMovies, isLoading, error, refetch } = useTop100PopularMovies();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  React.useEffect(() => {
    checkScrollButtons();
    const handleResize = () => checkScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkScrollButtons, popularMovies]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className={`relative ${className}`}>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-4 px-4 md:px-8">
          Top 100 Popular Movies
        </h2>
        <LoadingSkeleton />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={`relative ${className}`}>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-4 px-4 md:px-8">
          Top 100 Popular Movies
        </h2>
        <ErrorState onRetry={refetch} />
      </div>
    );
  }

  // Handle empty state
  if (!popularMovies || popularMovies.length === 0) {
    return (
      <div className={`relative ${className}`}>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-4 px-4 md:px-8">
          Top 100 Popular Movies
        </h2>
        <div className="flex items-center justify-center py-12 px-4">
          <p className="text-gray-400 text-center">No popular movies available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="mb-4 px-4 md:px-8">
        <h2 className="text-xl md:text-2xl font-bold text-white">
          Top 100 Popular Movies
        </h2>
      </div>
      
      <div className="relative group">
        {/* Left scroll button */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            aria-label="Scroll left"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Right scroll button */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            aria-label="Scroll right"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        {/* Content container with optimized rendering */}
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-4 md:px-8 pb-4"
          onScroll={checkScrollButtons}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {popularMovies.map((item, index) => (
            <div key={item.id} className="flex-none w-48 sm:w-56 md:w-64 relative">
              <ContentCard
                content={item}
                onClick={onContentClick}
                onPersonClick={onPersonClick}
                showPopularityScore={true}
              />
              {/* Popularity ranking badge */}
              <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-2 py-1 rounded-full shadow-lg z-10">
                #{index + 1}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PopularMoviesCarousel;